<template>
  <b-overlay class="loader" :no-wrap="true" :no-center="true" :show="isShow" fixed>
    <template #overlay>
      <div class="loader">
        <div class="wrap-custom-progress">
          <h1 class="mb-3">Creating Labels</h1>
          <b-progress height="45px" :max="max">
            <span class="progress-value">{{ progress }} %</span>
            <b-progress-bar :value="progress" />
          </b-progress>
          <p class="mt-1 text-xxs" v-text="message ? message : 'Waiting for the server to start processing.'" />
          <p class="text-center mt-4">This can take up to several minutes, depending on how busy the server is.</p>
          <p class="overlay-bottom-text">
            You don't have to wait on this page while it loads, feel free to go to
            <router-link :to="{ name: assignmentsRoute }" @click="closeLoader">Assignments List</router-link>
          </p>
        </div>
      </div>
    </template>
  </b-overlay>
</template>

<script lang="ts">
import type { UILoader } from '@/types/Store'
import { notify } from '@kyvg/vue3-notification'
import { BOverlay, BProgress, BProgressBar } from 'bootstrap-vue-next'
import { defineComponent, onBeforeUnmount, ref, watch, computed, type ComputedRef, type Ref } from 'vue'
import { LABELS_CREATED_SUCCESS } from '@/const/LabelsCreatedStatuses'
import { LABELS_CREATING_LOADER } from '@/const/LoadersNames'
import { checkMessage } from '@/services/WebSockets'
import { useAccountStore } from '@/store/account'
import { useAssignmentStore } from '@/store/assignment'
import { useAssignmentSummaryStore } from '@/store/assignmentSummary'
import { useUiStore } from '@/store/ui'
import { CUSTOMER_ASSIGNMENTS_LIST, STAFF_ASSIGNMENTS_LIST } from '@/types/router'

export default defineComponent({
  name: 'CreatingLabelsLoader',
  components: { BProgressBar, BProgress, BOverlay },
  setup() {
    const uiStore = useUiStore()
    const accountStore = useAccountStore()
    const assignmentStore = useAssignmentStore()
    const assignmentSummaryStore = useAssignmentSummaryStore()
    const max: Ref<number> = ref(100)
    const progress: Ref<number> = ref(0)
    const message: Ref<string | null> = ref(null)
    const loader: ComputedRef<UILoader | null> = computed(() => uiStore.loader ?? null)
    const assignmentId: ComputedRef<string> = computed(() => assignmentSummaryStore.id || '')
    const isStaff: ComputedRef<boolean> = computed(() => accountStore.isStaff)
    const isShow: ComputedRef<boolean> = computed(
      () => loader.value?.name === LABELS_CREATING_LOADER && Boolean(loader.value?.show),
    )

    const assignmentsRoute = computed(() => (isStaff.value ? STAFF_ASSIGNMENTS_LIST : CUSTOMER_ASSIGNMENTS_LIST))

    const closeLoader = () => {
      uiStore.loaderHide({ name: LABELS_CREATING_LOADER })
    }

    const parseLoaderProgress = (response: any) => {
      progress.value = progress.value === 100 ? 100 : response.progress
      message.value = typeof response.message !== 'undefined' ? response.message : null
    }

    watch(
      assignmentId,
      () => {
        if (assignmentId.value) {
          window.Echo.private(`App.Assignment.${assignmentId.value}`).listen(
            '.creating-labels-progress',
            (response: any) => parseLoaderProgress(response),
          )
        }
      },
      { immediate: true },
    )

    onBeforeUnmount(() => {
      if (assignmentId.value) {
        window.Echo.private(`App.Assignment.${assignmentId.value}`).stopListening('.creating-labels-progress')
      }
    })

    watch(isShow, (val) => {
      progress.value = 0
      if (val) {
        checkMessage(message, progress)
      }
    })

    watch(progress, (val) => {
      if (val === 100 && isShow.value) {
        setTimeout(() => {
          uiStore.loaderHide()
          assignmentStore.refreshItems()
          assignmentSummaryStore.fetchStatus()
          assignmentSummaryStore.fetchLabelsStatuses().then(() => {
            if (assignmentSummaryStore.labelsCreatedStatus === LABELS_CREATED_SUCCESS) {
              notify({ group: 'success', text: 'Labels has been created' })
            } else {
              notify({
                group: 'error',
                text: 'Labels for some items were not able to be created, please try to re-create them',
              })
            }
          })
        }, 1000)
      }
    })

    return {
      max,
      isShow,
      message,
      progress,
      assignmentsRoute,
      closeLoader,
    }
  },
})
</script>
